import React from 'react';
import ReactGridLayout, { getCompactor, type Layout } from 'react-grid-layout';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

export type GridItem = Layout[number];

interface GridLayoutProps {
    items: Layout;
    children: React.ReactNode;
    onLayoutChange?: (layout: Layout) => void;
    /** Parent must provide a definite height, for example through flex layout. */
    fillHeight?: boolean;
}

const BASE_ROW_HEIGHT = 52;
const GRID_MARGIN = 10;

const GridLayout = ({ items, children, onLayoutChange, fillHeight = false }: GridLayoutProps) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [size, setSize] = React.useState({ width: 0, height: 0 });

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver(([entry]) => {
            if (!entry) return;
            const width = Math.floor(entry.contentRect.width);
            const height = Math.floor(entry.contentRect.height);
            if (width <= 0) return;
            setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
        });

        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, []);

    const rows = items.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0);
    // Total grid height = rows * rowHeight + (rows - 1) * margin.
    // Observe the fixed-height shell, not the content, to avoid a resize loop.
    const rowHeight = fillHeight && rows > 0 ? Math.max(BASE_ROW_HEIGHT, (size.height - (rows - 1) * GRID_MARGIN) / rows) : BASE_ROW_HEIGHT;

    return (
        <div className="layout-shell" ref={containerRef} style={fillHeight ? { height: '100%', minHeight: 0 } : undefined}>
            {size.width > 0 && (
                <ReactGridLayout
                    className="layout"
                    width={size.width}
                    layout={items}
                    gridConfig={{
                        cols: 12,
                        margin: [GRID_MARGIN, GRID_MARGIN],
                        containerPadding: [0, 0],
                        rowHeight
                    }}
                    dragConfig={{ enabled: true, handle: '.grid-item-drag-icon' }}
                    resizeConfig={{ enabled: true }}
                    compactor={getCompactor('vertical', false, true)}
                    onLayoutChange={onLayoutChange}>
                    {children}
                </ReactGridLayout>
            )}
        </div>
    );
};

export default GridLayout;
